import { TeamName } from "@/components/TeamName";
import { type Appointment, type Task, isTask } from "@repo/api";
import { LightMode, Text, Variables } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  FlexDiv,
  OneLineInfo,
  localizeDate,
  localizeDateAndTime,
  localizeDateTimeRange,
  localizeDay,
  localizeWeekday,
} from "@repo/ui";
import {
  addDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  isToday,
  isTomorrow,
} from "date-fns";

import { isAppointment } from "../appointments/utils";

const getOverdueTime = (now: Date, date: Date) =>
  differenceInDays(now, date) > 0
    ? `${differenceInDays(now, date)} ${
        differenceInDays(now, date) === 1 ? t("label.day") : t("label.days")
      }`
    : differenceInHours(now, date) > 0
      ? `${differenceInHours(now, date)} ${
          differenceInHours(now, date) === 1
            ? t("label.hour")
            : t("label.hours")
        }`
      : `${differenceInMinutes(now, date)} ${
          differenceInMinutes(now, date) === 1
            ? t("label.minute")
            : t("label.minutes")
        }`;

const TaskTimeDisplay = ({ task }: { task: Task | Appointment }) => {
  const { dateFnsLocale } = useTranslation();
  const isDueDateTask =
    isTask(task) && task.isTaskDueBy === true && task.taskDate !== undefined;
  const isTimeSet = isTask(task) ? task.isTimeSet : true;
  const assignedCoworker = isTask(task) ? task.emailId : task.assignedCoworker;

  const assignedTeam = isTask(task) ? task.assignedTeam : task.assignedTeam;

  const startTime = isAppointment(task)
    ? task.timeWindowStartTime
    : isDueDateTask
      ? task.taskDate
      : task.taskStartDateTime;

  const endTime = isAppointment(task)
    ? task.timeWindowEndTime
    : isDueDateTask
      ? undefined
      : task.taskEndDateTime;

  const dueDate = isDueDateTask ? task.taskDate : undefined;

  const completedTime = isTask(task)
    ? task.completed?.taskCompletedDateTime
    : undefined;

  const completedBy = isTask(task) ? task.completed?.completedBy : undefined;

  const completedInfo =
    isTask(task) && task.isDone ? (
      <>
        {completedTime
          ? t("label.completed_at_date_time", {
              dateTime: localizeDateAndTime(completedTime, dateFnsLocale),
            })
          : ""}
        {completedBy ? ` | ` : ""}
        {completedBy && <CoWorkerName email={completedBy} />}
      </>
    ) : undefined;

  const assignedInfo = assignedCoworker ? (
    <CoWorkerName email={assignedCoworker} fragment />
  ) : assignedTeam ? (
    <TeamName teamId={assignedTeam} fragment />
  ) : undefined;

  const startDate = dueDate
    ? new Date(dueDate)
    : startTime
      ? new Date(startTime)
      : undefined;

  const now = new Date();

  const displayedTime = isTimeSet
    ? dueDate
      ? localizeDateAndTime(dueDate, dateFnsLocale)
      : localizeDateTimeRange(startTime, endTime, dateFnsLocale)
    : "";

  // No time set
  if (!isTimeSet || !startDate) {
    return completedInfo ?? <OneLineInfo small info={[assignedInfo]} />;
  }

  // Overdue
  if (startDate < now) {
    return (
      completedInfo ?? (
        <OneLineInfo
          small
          info={[
            assignedInfo,
            `${t("label.overdue")} (${getOverdueTime(now, startDate)})`,
            displayedTime,
          ]}
        />
      )
    );
  }

  // Today or Tomorrow
  if ((isToday(startDate) && startDate > now) || isTomorrow(startDate)) {
    return (
      completedInfo ?? (
        <OneLineInfo
          small
          info={[assignedInfo, `${localizeDay(startDate)}`, displayedTime]}
        />
      )
    );
  }

  // Within next 7 days
  if (
    startDate > endOfDay(addDays(now, 1)) &&
    startDate < endOfDay(addDays(now, 6))
  ) {
    return (
      completedInfo ?? (
        <OneLineInfo
          small
          info={[assignedInfo, `${localizeWeekday(startDate)}`, displayedTime]}
        />
      )
    );
  }

  // Beyond next 7 days
  if (startDate > endOfDay(addDays(now, 6))) {
    return (
      completedInfo ?? (
        <OneLineInfo
          small
          info={[
            assignedInfo,
            `${localizeDate(startDate, dateFnsLocale)}`,
            displayedTime,
          ]}
        />
      )
    );
  }

  return null;
};

export const TaskTimeInfo = ({ task }: { task: Task | Appointment }) => {
  return (
    <FlexDiv margin={`${Variables.space25} 0 0 0`}>
      <Text
        tagName="span"
        color={LightMode.colourTextAndIcon3}
        textStyle="Body.S"
      >
        <TaskTimeDisplay task={task} />
      </Text>
    </FlexDiv>
  );
};
